import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, User } from 'lucide-react';
import { Task } from '../../hooks/useTasks';
import TaskCard from './TaskCard';

interface ClientGroupProps {
  clientName: string;
  tasks: Task[];
  isExpanded: boolean;
  onToggle: () => void;
  onTaskClick: (task: Task) => void;
}

const ClientGroup: React.FC<ClientGroupProps> = ({
  clientName,
  tasks,
  isExpanded,
  onToggle,
  onTaskClick,
}) => {
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-2xl overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-[#0f172a] transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-[#9c68f2]/10 border border-[#9c68f2]/20 flex items-center justify-center">
            <User className="w-5 h-5 text-[#9c68f2]" />
          </div>
          <div className="text-left flex-1">
            <h3 className="text-[rgb(var(--fg))] font-semibold">{clientName}</h3>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-[rgb(var(--muted))]">
                {totalCount} task{totalCount !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <div className="w-24 h-1.5 bg-[rgb(var(--card))] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-[#9c68f2] rounded-full"
                  />
                </div>
                <span className="text-xs text-gray-500">{progress}%</span>
              </div>
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-[rgb(var(--muted))]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[rgb(var(--muted))]" />
        )}
      </button>

      {/* Tasks Grid */}
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4 pt-0"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => onTaskClick(task)}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ClientGroup;

